import { makeAutoObservable, runInAction } from 'mobx';
import { showToast } from '../../../utils/toast';
import { HQ_INITIAL, SUBSIDIARY_INITIAL } from '../constants/onboard.constant';
import { childCompaniesURL, multiCompanyURL } from '../../User/api/url';
import { HQ, SUBSIDIARY } from '../../../constants/commonText.constants';
import { handleFetch } from '../../../utils/handleFetch';

const buildCompanyForm = (level, parentHQId = '') => {
	const base = level === HQ ? HQ_INITIAL : SUBSIDIARY_INITIAL;
	const form = {
		_id: null,
		companyName: base.companyName || '',
		country: base.country || '',
		address: base.address || '',
		notes: '',
		isActive: true,
		cLevel: level,
	};

	if (level === SUBSIDIARY) {
		form.HQId = parentHQId || '';
	}

	return form;
};

class BoundaryStore {
	childCompanies = [];
	selectedNode = { type: 'parent', id: null, parentId: null };
	isCompanyModalOpen = false;
	isDeleteConfirmModalOpen = false;
	companyForm = buildCompanyForm(HQ);
	isTypeLocked = false;
	formErrors = {};
	deleteTargetIds = [];
	deleteTargetName = '';
	deleteTargetType = null;

	constructor() {
		makeAutoObservable(this);
	}

	getChildCompanies = async () => {
		return await handleFetch({
			url: childCompaniesURL,
			method: 'GET',
			onSuccess: (data) => {
				runInAction(() => {
					this.childCompanies = Array.isArray(data) ? data : [];
					this.ensureSelectedNode();
				});
			},
		});
	};

	getHQById = (id) => this.childCompanies.find((company) => company.cLevel === HQ && company._id === id);

	findSubsidiaryById = (id) => {
		const topLevel = this.childCompanies.find((company) => company.cLevel === SUBSIDIARY && company._id === id);
		if (topLevel) return topLevel;

		for (const hq of this.childCompanies.filter((company) => company.cLevel === HQ)) {
			const sub = hq.subsidiaries?.find((company) => company.cLevel === SUBSIDIARY && company._id === id);
			if (sub) return sub;
		}

		return null;
	};

	ensureSelectedNode = () => {
		if (!this.selectedNode) {
			this.selectedNode = { type: 'parent', id: null, parentId: null };
			return;
		}

		if (this.selectedNode.type === 'hq' && !this.getHQById(this.selectedNode.id)) {
			this.selectedNode = { type: 'parent', id: null, parentId: null };
		}

		if (this.selectedNode.type === 'subsidiary' && !this.findSubsidiaryById(this.selectedNode.id)) {
			this.selectedNode = { type: 'parent', id: null, parentId: null };
		}
	};

	selectNode = (node) => {
		this.selectedNode = node || { type: 'parent', id: null, parentId: null };
	};

	openCreateModal = (level, parentHQId = '', lockType = false) => {
		this.companyForm = buildCompanyForm(level, parentHQId);
		this.isTypeLocked = lockType;
		this.formErrors = {};
		this.isCompanyModalOpen = true;
	};

	openEditModal = (company, parentHQId = '') => {
		if (!company) return;

		const form = {
			_id: company._id || null,
			companyName: company.companyName || '',
			country: company.country || '',
			address: company.address || '',
			notes: company.notes || '',
			isActive: company.isActive ?? true,
			cLevel: company.cLevel,
		};

		if (company.cLevel === SUBSIDIARY) {
			form.HQId = parentHQId || company.HQId || '';
		}

		this.companyForm = form;
		this.isTypeLocked = true;
		this.formErrors = {};
		this.isCompanyModalOpen = true;
	};

	closeCompanyModal = () => {
		this.companyForm = buildCompanyForm(HQ);
		this.formErrors = {};
		this.isTypeLocked = false;
		this.isCompanyModalOpen = false;
	};

	setCompanyFormValue = (e) => {
		const { id, value } = e.target;
		this.companyForm[id] = value;
		if (id === 'cLevel' && value === SUBSIDIARY && this.companyForm.HQId === undefined) {
			this.companyForm.HQId = '';
		}
		if (this.formErrors[id]) {
			const { [id]: _removed, ...rest } = this.formErrors;
			this.formErrors = rest;
		}
	};

	saveCompany = async () => {
		const { _id, companyName, country, address, cLevel, HQId, notes, isActive } = this.companyForm;
		const payload = {
			_id,
			companyName,
			country,
			address,
			cLevel,
			notes,
			isActive,
		};

		if (cLevel === SUBSIDIARY) {
			payload.HQId = HQId || null;
		}

		const isEdit = Boolean(_id);

		return showToast.promise(
			(async () => {
				const success = await handleFetch({
					url: multiCompanyURL,
					method: isEdit ? 'PUT' : 'POST',
					body: [payload],
					onSuccess: () => {
						runInAction(() => {
							this.formErrors = {};
							this.closeCompanyModal();
						});
						this.getChildCompanies();
					},
					onValidationError: (errors) => runInAction(() => (this.formErrors = errors || {})),
				});
				if (!success) throw new Error('Failed to save company');
				return success;
			})(),
			{
				loading: isEdit ? 'Updating company...' : 'Creating company...',
				success: isEdit ? 'Company updated successfully!' : 'Company created successfully!',
				error: 'Failed to save company',
			},
		);
	};

	openDeleteConfirmModal = (company) => {
		if (!company?._id) return;

		const deleteIds = [company._id];
		if (company.cLevel === HQ) {
			const hq = this.getHQById(company._id) || company;
			hq.subsidiaries?.forEach((sub) => {
				if (sub?._id) deleteIds.push(sub._id);
			});
		}

		this.deleteTargetIds = [...new Set(deleteIds)];
		this.deleteTargetName = company.companyName || '';
		this.deleteTargetType = company.cLevel || null;
		this.isDeleteConfirmModalOpen = true;
	};

	closeDeleteConfirmModal = () => {
		this.isDeleteConfirmModalOpen = false;
		this.deleteTargetIds = [];
		this.deleteTargetName = '';
		this.deleteTargetType = null;
	};

	confirmDeleteCompany = async () => {
		if (!this.deleteTargetIds.length) return false;

		return showToast.promise(
			(async () => {
				const success = await handleFetch({
					url: multiCompanyURL,
					method: 'DELETE',
					body: this.deleteTargetIds,
					onSuccess: () => {
						runInAction(() => {
							this.closeDeleteConfirmModal();
							this.selectNode({ type: 'parent', id: null, parentId: null });
						});
						this.getChildCompanies();
					},
				});
				if (!success) throw new Error('Failed to delete company');
				return success;
			})(),
			{
				loading: 'Deleting company...',
				success: 'Company deleted successfully!',
				error: 'Failed to delete company',
			},
		);
	};
}

export const boundaryStore = new BoundaryStore();
